import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/* ─── Types ──────────────────────────────────────────────────────────────── */

export interface EnrichedClient {
  id: number;
  name: string;
  filename?: string;
  country?: string;
  originalCurrency: string;
  totalInvoiced: number;              // USD
  totalInvoicedBaseCurrency: number;
  outstanding: number;                // USD
  outstandingBaseCurrency: number;
  deposit: number;                    // USD
  depositBaseCurrency: number;
  depositAdjustment: number;
  depositAdjustmentBaseCurrency: number;
  netDeposit: number;
  netDepositBaseCurrency: number;
  riskLevel: 'Low' | 'Medium' | 'High';
  paymentRatio: number;
}

export type SortField =
  | 'name'
  | 'totalInvoiced'
  | 'totalInvoicedBaseCurrency'
  | 'outstanding'
  | 'outstandingBaseCurrency'
  | 'deposit'
  | 'depositAdjustment'
  | 'netDeposit';

export type SortDir = 'asc' | 'desc';

export interface ClientManagementTableProps {
  clients: EnrichedClient[];
  sortBy: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
  onClientClick: (client: EnrichedClient) => void;
  loading?: boolean;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const fmtUSD = (n: number): string => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtBase = (n: number, ccy: string): string =>
  `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${ccy}`;


/* ─── Sort header cell ───────────────────────────────────────────────────── */

interface ThProps {
  field: SortField;
  label: React.ReactNode;
  current: SortField;
  dir: SortDir;
  onSort: (f: SortField) => void;
  className?: string;
}

const Th: React.FC<ThProps> = ({ field, label, current, dir, onSort, className = '' }) => {
  const active = current === field;
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap cursor-pointer select-none hover:text-slate-700 ${className}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={12} className={active ? 'text-blue-500' : 'text-slate-300'} />
      </span>
    </th>
  );
};

/* ─── Empty state ────────────────────────────────────────────────────────── */

const EmptyState: React.FC = () => (
  <tr>
    <td colSpan={8} className="text-center py-16 text-slate-600 text-sm">
      No clients match your filters.
    </td>
  </tr>
);

/* ─── Skeleton loader ────────────────────────────────────────────────────── */

const SkeletonRows: React.FC = () => (
  <>
    {[...Array(6)].map((_, i) => (
      <tr key={i} className="border-t border-slate-100">
        {[...Array(8)].map((__, j) => (
          <td key={j} className="px-3 py-3">
            <div className="h-3.5 bg-slate-100 rounded animate-pulse" style={{ width: j === 1 ? '70%' : '50%' }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* ─── Main Table ─────────────────────────────────────────────────────────── */

export const ClientManagementTable: React.FC<ClientManagementTableProps> = ({
  clients,
  sortBy,
  sortDir,
  onSort,
  onClientClick,
  loading = false,
}) => {

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b-2 border-slate-200">
            <tr>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-10">
                S.No
              </th>
              <Th field="name" label="Client" current={sortBy} dir={sortDir} onSort={onSort} className="min-w-[180px]" />
              <Th
                field="totalInvoicedBaseCurrency"
                label="Invoiced"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
                className="min-w-[140px]"
              />
              <Th
                field="outstanding"
                label="Outstanding"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
                className="min-w-[140px]"
              />
              <Th
                field="deposit"
                label="Total Deposit"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
                className="min-w-[130px]"
              />
              <Th
                field="depositAdjustment"
                label="Deposit Adj."
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
                className="min-w-[130px]"
              />
              <Th
                field="netDeposit"
                label="Deposit Balance"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
                className="min-w-[130px]"
              />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : clients.length === 0 ? (
              <EmptyState />
            ) : (
              clients.map((client, idx) => (
                <tr
                  key={client.id}
                  className={`border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60 ${idx % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'}`}
                  onClick={() => onClientClick(client)}
                >
                  {/* S.No */}
                  <td className="px-3 py-2.5 text-slate-600 tabular-nums text-xs">{idx + 1}</td>

                  {/* Client Name + currency / country */}
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-slate-800 leading-tight">{client.name}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      <span className="font-medium tabular-nums">{client.originalCurrency}</span>
                      {client.country ? (
                        <>
                          <span
                            className="mx-1.5 inline-block translate-y-[-0.06em] text-[12px] font-black leading-none text-blue-600"
                            aria-hidden
                          >
                            ·
                          </span>
                          <span>{client.country}</span>
                        </>
                      ) : null}
                    </div>
                  </td>

                  {/* Invoiced base / USD */}
                  <td className="px-3 py-2.5 tabular-nums">
                    <div className="text-slate-700 leading-tight">
                      {fmtBase(client.totalInvoicedBaseCurrency, client.originalCurrency)}
                    </div>
                    <div className="text-[11px] text-slate-600">{fmtUSD(client.totalInvoiced)}</div>
                  </td>

                  {/* Outstanding base / USD */}
                  <td className="px-3 py-2.5 tabular-nums">
                    <div className={`leading-tight ${client.outstanding > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
                      {fmtBase(client.outstandingBaseCurrency, client.originalCurrency)}
                    </div>
                    <div className="text-[11px] text-slate-600">{fmtUSD(client.outstanding)}</div>
                  </td>

                  {/* Deposit base / USD */}
                  <td className="px-3 py-2.5 tabular-nums">
                    <div className="text-slate-700 leading-tight">
                      {fmtBase(client.depositBaseCurrency, client.originalCurrency)}
                    </div>
                    <div className="text-[11px] text-slate-600">{fmtUSD(client.deposit)}</div>
                  </td>

                  {/* Deposit adj base / USD */}
                  <td className="px-3 py-2.5 tabular-nums">
                    <div
                      className={`leading-tight ${client.depositAdjustment < 0 ? 'text-red-600' : 'text-slate-700'}`}
                    >
                      {fmtBase(client.depositAdjustmentBaseCurrency, client.originalCurrency)}
                    </div>
                    <div className={`text-[11px] ${client.depositAdjustment < 0 ? 'text-red-500' : 'text-slate-600'}`}>
                      {fmtUSD(client.depositAdjustment)}
                    </div>
                  </td>

                  {/* Net deposit base / USD */}
                  <td className="px-3 py-2.5 tabular-nums">
                    <div
                      className={`leading-tight ${client.netDeposit < 0 ? 'text-red-600' : 'text-emerald-700'}`}
                    >
                      {fmtBase(client.netDepositBaseCurrency, client.originalCurrency)}
                    </div>
                    <div className={`text-[11px] ${client.netDeposit < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                      {fmtUSD(client.netDeposit)}
                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>

        </table>
      </div>
    </div>
  );
};

export default ClientManagementTable;
